import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Clock, Play, RefreshCw, Calendar, 
  CheckCircle, XCircle, AlertCircle, Timer
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import Sidebar from '../components/Sidebar';

export default function SchedulerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      toast.error('Access denied. Super admin only.');
      navigate('/dashboard');
      return;
    }
    fetchStatus();
  }, [user, navigate]);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get('/scheduler/status');
      setStatus(response.data);
    } catch (error) {
      toast.error('Failed to load scheduler status');
    } finally {
      setLoading(false);
    }
  };

  const triggerJob = async (jobName) => {
    setTriggering(true);
    try {
      const response = await api.post(`/scheduler/trigger/${jobName}`);
      toast.success(response.data.message);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger job');
    } finally {
      setTriggering(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />

      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight flex items-center gap-2">
              <Timer className="h-6 w-6 text-primary" />
              Scheduled Jobs
            </h1>
            <p className="font-body text-muted-foreground">Manage automated tasks</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStatus}
            data-testid="refresh-status"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Scheduler Status */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Scheduler Status
              </CardTitle>
              <Badge className={status?.scheduler_running ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                {status?.scheduler_running ? (
                  <><CheckCircle className="h-3 w-3 mr-1" /> Running</>
                ) : (
                  <><XCircle className="h-3 w-3 mr-1" /> Stopped</>
                )}
              </Badge>
            </div>
            <CardDescription>
              Automated background tasks for the application
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Scheduled Jobs */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Scheduled Jobs</CardTitle>
            <CardDescription>
              Configure and manage automated tasks
            </CardDescription>
          </CardHeader>
          <CardContent>
            {status?.jobs?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No scheduled jobs configured</p>
              </div>
            ) : (
              <div className="space-y-4">
                {status?.jobs?.map((job) => (
                  <div
                    key={job.id}
                    className="flex items-center justify-between p-4 rounded-lg border bg-card"
                    data-testid={`job-${job.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-full bg-primary/10">
                        <Calendar className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium">{job.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          Schedule: {job.trigger}
                        </p>
                        {job.next_run && (
                          <p className="text-sm text-muted-foreground">
                            Next run: {formatDate(job.next_run)}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => triggerJob(job.id)}
                      disabled={triggering}
                      data-testid={`trigger-${job.id}`}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Run Now
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Runs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Job Runs</CardTitle>
            <CardDescription>
              History of scheduled job executions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {status?.recent_runs?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No recent job runs</p>
              </div>
            ) : (
              <div className="space-y-3">
                {status?.recent_runs?.map((run, index) => (
                  <div
                    key={run.id || index}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card"
                    data-testid={`run-${run.id || index}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-1.5 rounded-full ${
                        run.status === 'completed' 
                          ? 'bg-green-100 text-green-600' 
                          : 'bg-red-100 text-red-600'
                      }`}>
                        {run.status === 'completed' ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <XCircle className="h-4 w-4" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{run.job_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {run.reminders_sent !== undefined && `${run.reminders_sent} reminders sent`}
                        </p>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {formatDate(run.run_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
